// auth imported from firebase if needed for future use

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'An unknown API error occurred.');
  }
  return data;
};

// ===================================================================
//  PORTFOLIO & TRANSACTION APIS (Portfolio-Specific)
// ===================================================================

export const getPortfolioSummary = (token, portfolioId) => {
  return fetch(`${API_URL}/api/portfolios/${portfolioId}/summary`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(handleResponse);
};

export const recalculatePortfolioSummary = async (token, portfolioId) => {
  const response = await fetch(`${API_URL}/api/portfolios/${portfolioId}/recalculate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'API call failed');
  }
  return response.json(); // Returns the { status: "success", ... } message
};

export const fetchTransactions = (token, portfolioId) => {
  return fetch(`${API_URL}/api/portfolios/${portfolioId}/transactions`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(handleResponse);
};

export const deleteTransaction = (token, portfolioId, transactionId) => {
  return fetch(`${API_URL}/api/portfolios/${portfolioId}/transactions/${transactionId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(handleResponse);
};

export const postManualTrade = (token, portfolioId, trade) => {
  return fetch(`${API_URL}/api/portfolios/${portfolioId}/manual_trade`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ trade }),
  }).then(handleResponse);
};

export const uploadTransactions = (token, portfolioId, transactionsData) => {
  return fetch(`${API_URL}/api/portfolios/${portfolioId}/transactions/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ transactions: transactionsData }),
  }).then(handleResponse);
};

export const deleteAllPortfolioTransactions = async (token, portfolioId) => {
  const confirmation = window.confirm(
    `Are you sure you want to delete ALL transactions from this portfolio? This action cannot be undone.`
  );
  if (!confirmation) {
    throw new Error('Deletion cancelled by user.');
  }

  const response = await fetch(`${API_URL}/api/portfolios/${portfolioId}/transactions/delete_all`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return handleResponse(response);
};

export const deletePortfolio = async (token, portfolioId) => {
  // We'll put the confirmation dialog here in the API layer
  const confirmation = window.confirm(
    `ARE YOU SURE you want to permanently delete this portfolio and all of its transactions? This action cannot be undone.`
  );
  if (!confirmation) {
    throw new Error('Deletion cancelled by user.');
  }

  const response = await fetch(`${API_URL}/api/portfolios/${portfolioId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return handleResponse(response);
};

export const updatePortfolio = async (token, portfolioId, portfolioData) => {
  const response = await fetch(`${API_URL}/api/portfolios/${portfolioId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(portfolioData) // e.g., { name: "New Name", description: "..." }
  });
  
  return handleResponse(response);
};
// ===================================================================
//  WATCHLIST APIS (User-Specific)
// ===================================================================

export const getWatchlist = (token) => {
  // The URL no longer needs the userId
  return fetch(`${API_URL}/api/watchlist`, { 
    headers: { 'Authorization': `Bearer ${token}` } 
  }).then(handleResponse);
};

export const addToWatchlist = (token, ticker) => {
  // The URL no longer needs the userId
  return fetch(`${API_URL}/api/watchlist`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticker }),
  }).then(handleResponse);
};

export const deleteFromWatchlist = async (token, userId, ticker) => {
  const response = await fetch(`${API_URL}/api/watchlist/${userId}/${ticker}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete ticker from watchlist');
  }
  return data;
};

export const importFromPortfolio = (token, portfolioId) => {
  const body = portfolioId ? JSON.stringify({ portfolio_id: portfolioId }) : undefined;
  return fetch(`${API_URL}/api/watchlist/import`, {
    method: 'POST',
    headers: { 
      'Authorization': `Bearer ${token}`,
      ...(portfolioId && { 'Content-Type': 'application/json' })
    },
    ...(body && { body })
  }).then(handleResponse);
};

export const getWatchlistItemData = async (token, ticker) => {
  const response = await fetch(`${API_URL}/api/watchlist/item/${ticker}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

// ===================================================================
//  USER & AUTHENTICATION APIS
// ===================================================================

export const initializeNewUser = (token, displayName, handle) => { // <-- Step 1: Accept displayName
  return fetch(`${API_URL}/api/initialize_new_user`, {
    method: 'POST',
    headers: { 
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json' // <-- Step 2: Add Content-Type header
    },
    // Step 3: Send the displayName in the body
    body: JSON.stringify({ displayName: displayName, handle: handle  }) 
  }).then(handleResponse);
};

export const deleteAllUserData = async (token, collectionName) => {
  const confirmation = window.confirm(`Are you sure you want to delete ALL items from your ${collectionName}? This action cannot be undone.`);
  if (!confirmation) throw new Error('Deletion cancelled by user.');

  return fetch(`${API_URL}/api/user_data/delete_all`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ collection: collectionName })
  }).then(handleResponse);
};

// ===================================================================
//  CONTENT & SOCIAL APIS
// ===================================================================

export const getPublicSummaries = () => fetch(`${API_URL}/api/summaries/public`).then(handleResponse);
//export const getSummaryDetails = (summaryId) => fetch(`${API_URL}/api/summary_details/${summaryId}`).then(handleResponse);
export const getDiscussionTopics = () => fetch(`${API_URL}/api/discussion/topics`).then(handleResponse);
export const getComments = (entityType, entityId) => fetch(`${API_URL}/api/comments/${entityType}/${entityId}`).then(handleResponse);
export const getPersonalizedHubData = (token) => fetch(`${API_URL}/api/hub/for_user`, { headers: { 'Authorization': `Bearer ${token}` } }).then(handleResponse);

export const postComment = (token, commentData) => {
  return fetch(`${API_URL}/api/comments`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(commentData),
  }).then(handleResponse);
};

export const summarizeContent = (formData) => {
  // The function should ONLY be responsible for sending the request.
  // It should not modify the formData object.
  return fetch(`${API_URL}/api/summarize`, {
    method: 'POST',
    body: formData,
  }).then(handleResponse);
};

// --- THIS FUNCTION IS NOW RESTORED ---
export const getTagHubData = (token, tag) => {
  return fetch(`${API_URL}/api/hub/search/${tag}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(handleResponse);
}

// ===================================================================
//  FINANCIAL DATA & ANALYSIS APIS
// ===================================================================

export const getHistoricalData = (ticker) => fetch(`${API_URL}/api/historical/${ticker}`).then(handleResponse);

export const getDeepStats = (ticker) => fetch(`${API_URL}/api/analysis/deep_stats/${ticker}`).then(handleResponse);

// --- THIS FUNCTION IS NOW RESTORED ---
// Note: This function probably needs to be updated to be portfolio-specific
export const postCommand = (token, portfolioId, command) => {
  return fetch(`${API_URL}/api/portfolios/${portfolioId}/command`, { // Updated to be portfolio-specific
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ command }),
  }).then(handleResponse);
};

// ===================================================================
//  AI LAB APIS
// ===================================================================

export const executeAiScreener = (token, query) => fetch(`${API_URL}/api/ai_lab/screener`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ query }),
}).then(handleResponse);

export const analyzeScreenerResults = (token, results, tool_name) => fetch(`${API_URL}/api/ai_lab/analyze_results`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ results, tool_name }),
}).then(handleResponse);

export const continueAnalysis = (token, data_table, history) => fetch(`${API_URL}/api/ai_lab/continue_analysis`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ data_table, history }),
}).then(handleResponse);

export const analyzeDeepDiveStats = (token, ticker) => fetch(`${API_URL}/api/ai_lab/analyze_deep_dive`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ ticker })
}).then(handleResponse);

export const getDivergenceAnalysis = async (token, params) => {
  // params is an object like { tickerA: 'NVDA', tickerB: 'QQQ', startDate: '...', ... }

  // --- THIS IS THE KEY FIX ---
  // We will now correctly build the URL string with all the parameters.
  
  // 1. Get the main ticker for the URL path.
  const ticker = params.tickerA;

  // 2. Build the query string from the other parameters.
  const queryParams = new URLSearchParams({
    benchmark: params.tickerB,
    threshold: params.threshold / 100.0, // Convert percentage to decimal for the backend
    startDate: params.startDate,
    endDate: params.endDate,
    alphaLookbackDays: params.alphaLookbackDays
  });

  // 3. Combine them into the final, correct URL.
  const url = `${API_URL}/api/divergence_analysis/${ticker}?${queryParams.toString()}`;
  // Example result: /api/divergence_analysis/NVDA?benchmark=QQQ&threshold=0.001&...

  // 4. Make the fetch call with the correct URL.
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` }
  });

  // The rest of the function remains the same.
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to get divergence analysis.');
  }
  return data;
};

// Update the signature to include the alphaMetric

export const analyzeComparison = async (token, params, costsCredit) => {
  // It takes the params object and the boolean flag as separate arguments
  
  const response = await fetch(`${API_URL}/api/ai_lab/analyze_comparison`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    // It combines them into a single JSON object for the backend
    body: JSON.stringify({ ...params, costs_credit: costsCredit }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'API call for analysis failed.');
  }
  return data;
};

// --- Pairs Trading ---
export const getPairsAnalysis = (token, asset1, asset2) => fetch(`${API_URL}/api/ai_lab/pairs_analysis?asset1=${asset1}&asset2=${asset2}`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

export const saveWatchedPair = (token, pairData) => fetch(`${API_URL}/api/pairs/save`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ pair_data: pairData }),
}).then(handleResponse);

export const getPairsMonitorData = (token) => fetch(`${API_URL}/api/pairs/monitor_data`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

// ===================================================================
//  PAYMENT APIS
// ===================================================================

export const createCheckoutSession = (token, priceId) => fetch(`${API_URL}/api/payments/create-checkout-session`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ priceId: priceId }),
}).then(handleResponse);

export const fetchPortfolios = async (token, userId) => {
  const response = await fetch(`${API_URL}/api/portfolios`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch portfolios');
  }
  return response.json();
};

export const createNewPortfolio = async (token, portfolioData) => {
  // portfolioData is an object like { name: "My Portfolio", description: "..." }
  
  const response = await fetch(`${API_URL}/api/portfolios`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    // We pass the portfolioData object directly in the body
    body: JSON.stringify(portfolioData),
  });

  const data = await response.json();

  if (!response.ok) {
    // If the server returned an error, throw it so the component can catch it
    throw new Error(data.error || 'Failed to create portfolio.');
  }
  
  // If successful, return the JSON data from the backend.
  // This will be the new portfolio object.
  return data;
};

export const getNewsImpactAnalysis = async (token, ticker, articles) => {
  const response = await fetch(`${API_URL}/api/news/impact_analysis`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ticker, articles }),
  });

  // We can reuse the handleResponse helper if you have one, or handle it directly
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'API call for impact analysis failed.');
  }
  return data;
};

// financial service

export const getStockSummary = async (ticker) => {
  // This endpoint is public and doesn't require an auth token.
  const response = await fetch(`${API_URL}/api/stock_summary/${ticker}`);

  const data = await response.json();
  if (!response.ok) {
    // Pass the error message from the backend to the frontend
    throw new Error(data.error || 'Failed to fetch stock summary.');
  }
  return data;
};

export const getStockChart = async (ticker, period = '1Y') => {
  const response = await fetch(`${API_URL}/api/chart/${ticker}?period=${period}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch chart data.');
  return data;
};

export const getStockHeadlines = async (ticker) => {
  const response = await fetch(`${API_URL}/api/news/headlines/${ticker}`);
  const data = await response.json();
  return data;
};

//financial


//Portfolio


//ai services


//strategies

export const runPairsBacktest = async (token, params) => { // <-- It needs the token here
  const response = await fetch(`${API_URL}/api/backtest/pairs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}` // And it needs to use it here
    },
    body: JSON.stringify(params),
  });
  
  return handleResponse(response);
};

export const analyzeBacktestResults = async (kpis, pairString) => {
  const response = await fetch(`${API_URL}/api/ai_lab/analyze_backtest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kpis, pairString }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'AI analysis failed.');
  return data;
};

/**
 * Runs the Alpha Portfolio backtest. This is an authenticated request.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} params - The backtest parameters from the component.
 * @returns {Promise<object>} The backtest results.
 */
export const runAlphaPortfolioBacktest = (token, params) => {
  return fetch(`${API_URL}/api/backtest/alpha_portfolio`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  }).then(handleResponse);
};

/**
 * Requests an AI-powered analysis of the backtest results.
 * @param {string} token - The user's Firebase auth token.
 * @param {object} kpis - The Key Performance Indicators from the backtest.
 * @param {string} pairString - A descriptive name for the backtest (e.g., "Alpha Portfolio (SP100) vs. SPY").
 * @returns {Promise<object>} The AI analysis.
 */
export const getBacktestAnalysis = (token, kpis, pairString) => {
  return fetch(`${API_URL}/api/ai_lab/analyze_backtest`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ kpis, pairString }),
  }).then(handleResponse);
};

export const exportBacktestToPortfolio = (token, name, trades) => {
  return fetch(`${API_URL}/api/backtest/export_to_portfolio`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, trades }),
  }).then(handleResponse);
};

export const getAlphaDecaySignals = (token, params, costsCredit) => {
  return fetch(`${API_URL}/api/trading_signals/alpha_decay`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    // Send the params and the new flag with the correct key name
    body: JSON.stringify({ params, costs_credit: costsCredit }),
  }).then(handleResponse);
};

export const deductAiCredit = (token) => {
  return fetch(`${API_URL}/api/user/deduct_credit`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(handleResponse);
};

export const getRelatedCompanies = async (token, ticker, articles) => {
  const response = await fetch(`${API_URL}/api/news/related_companies`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ticker, articles }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'API call for related companies failed.');
  }
  return data;
};

export const analyzePortfolioHealth = async (token) => {
  // NOTE: Your backend endpoint doesn't need a portfolioId because it analyzes
  // the user's overall summary. We'll adjust if this changes.
  const response = await fetch(`${API_URL}/api/ai_lab/portfolio_analysis`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    // The backend doesn't require a body for this specific endpoint, just the token
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'AI portfolio analysis failed.');
  }
  return data;
};

export const createStripeCheckoutSession = async (token, priceId) => {
  // It now accepts a simple string `priceId`
  const response = await fetch(`${API_URL}/api/payments/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    // It correctly packages it into a JSON object with the key 'priceId'
    body: JSON.stringify({ priceId: priceId }),
  });
  
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error?.message || 'Failed to create checkout session.');
  }
  return data;
};

export const runAiScreener = (token, query, costsCredit) => {
  return fetch(`${API_URL}/api/ai_lab/screener`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, costs_credit: costsCredit }), // Send the flag
  }).then(handleResponse);
};

// In frontend/src/services/api.js - ADD THIS NEW FUNCTION

// In frontend/src/services/api.js - REPLACE this function

// --- The function signature now correctly includes the 'isPublic' parameter ---
export const runAiResearch = async (token, query, isPublic) => {
  const response = await fetch(`${API_URL}/api/ai_lab/research_assistant`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    // It now correctly sends the query AND the isPublic flag
    body: JSON.stringify({ query: query, isPublic: isPublic }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'The AI research assistant failed.');
  }

  return data;
};

export const getReviewQueue = async (token) => {
  const response = await fetch(`${API_URL}/api/admin/review_queue`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return handleResponse(response);
};

export const getSummaryDetails = async (token, summaryId) => {
  // This function now REQUIRES a token.
  if (!token || !summaryId) {
    throw new Error("Token and Summary ID are required.");
  }
  const response = await fetch(`${API_URL}/api/summaries/${summaryId}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return handleResponse(response);
};

export const updateSummary = async (token, summaryId, summaryData) => {
  const response = await fetch(`${API_URL}/api/admin/summaries/${summaryId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(summaryData),
  });
  return handleResponse(response);
};

export const searchAdminSummaries = async (token, searchTerm) => {
  const response = await fetch(`${API_URL}/api/admin/search_summaries?q=${searchTerm}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  return handleResponse(response);
};

export const getGroupMessages = async (token, groupId) => {
  if (!groupId) {
    throw new Error("Group ID is required to fetch messages.");
  }
  
  const response = await fetch(`${API_URL}/api/chat/groups/${groupId}/messages`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  
  // Assuming you have a handleResponse helper
  return handleResponse(response);
};

export const getMyChatGroups = async (token) => {
  const response = await fetch(`${API_URL}/api/chat/my-groups`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

export const createChatGroup = async (token, groupData) => {
  // groupData will be an object like { name: "My New Group", description: "..." }
  const response = await fetch(`${API_URL}/api/chat/groups`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(groupData)
  });
  return handleResponse(response);
};



/**
 * Fetches the full details of a single chat group, including its member list.
 * @param {string} token - The user's Firebase auth token.
 * @param {string} groupId - The ID of the group to fetch.
 * @returns {Promise<object>} The group data object.
 */
export const getGroupDetails = async (token, groupId) => {
  const response = await fetch(`${API_URL}/api/chat/groups/${groupId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

/**
 * Sends an invitation for a user to join a group.
 * @param {string} token - The user's Firebase auth token.
 * @param {string} groupId - The ID of the group to invite someone to.
 * @param {string} handle - The unique @handle of the user to invite.
 * @returns {Promise<object>} The success message from the API.
 */
export const sendGroupInvitation = async (token, groupId, handle) => {
  const response = await fetch(`${API_URL}/api/chat/groups/${groupId}/invitations`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ handle: handle })
  });
  return handleResponse(response);
};

/**
 * Fetches the current user's pending group invitations.
 * @param {string} token - The user's Firebase auth token.
 * @returns {Promise<Array>} A list of invitation objects.
 */
export const getMyInvitations = async (token) => {
  const response = await fetch(`${API_URL}/api/my-invitations`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

/**
 * Responds to a group invitation.
 * @param {string} token - The user's Firebase auth token.
 * @param {string} invitationId - The ID of the invitation to respond to.
 * @param {string} userResponse - The user's response, either "accept" or "decline". // <-- RENAMED PARAMETER
 * @returns {Promise<object>} The success message from the API.
 */
export const respondToInvitation = async (token, invitationId, userResponse) => { // <-- RENAMED HERE
  // This 'response' variable is for the HTTP fetch response. It no longer conflicts.
  const response = await fetch(`${API_URL}/api/invitations/${invitationId}/respond`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    // We send the renamed parameter in the body with the key the backend expects ('response').
    body: JSON.stringify({ response: userResponse }) 
  });
  return handleResponse(response);
};

export const getMyProfile = async (token) => {
  const response = await fetch(`${API_URL}/api/profile/me`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return handleResponse(response);
};

export const updateMyProfile = async (token, profileData) => {
  const response = await fetch(`${API_URL}/api/profile/me`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(profileData)
  });
  return handleResponse(response);
};

export const getUserByHandle = async (handle) => {
  const response = await fetch(`${API_URL}/api/users/handle/${handle}`);
  return handleResponse(response);
};



export const getUserPosts = async (handle) => {
  const response = await fetch(`${API_URL}/api/users/handle/${handle}/posts`);
  return handleResponse(response);
};

export const createPost = async (token, content, imageUrl) => {
  const response = await fetch(`${API_URL}/api/posts`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    // --- CHECK THIS LINE CAREFULLY ---
    body: JSON.stringify({ content, imageUrl: imageUrl || null }) 
  });
  return handleResponse(response);
};

export const getWatchlistItemDetails = async (token, ticker) => {
  const response = await fetch(`${API_URL}/api/watchlist/item_details/${ticker}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch item details.');
  }
  return response.json();
};


// ===================================================================
//  BROADRIDGE-INSPIRED FEATURES
// ===================================================================

// Reference Data: Peer comparison
export const getStockPeers = (ticker) => fetch(`${API_URL}/api/stock_summary/${ticker}/peers`).then(handleResponse);

// Trade Lifecycle: Cost basis, realized/unrealized P&L, trade stats
export const getTradeAnalytics = (token, portfolioId, method = 'FIFO') => fetch(`${API_URL}/api/portfolios/${portfolioId}/trade_analytics?method=${method}`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

// Portfolio Analytics
export const getAnalyticsOverview = (token, portfolioId, period = '1Y') => fetch(`${API_URL}/api/portfolios/${portfolioId}/analytics/overview?period=${period}`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

export const getAnalyticsAttribution = (token, portfolioId, period = '1Y') => fetch(`${API_URL}/api/portfolios/${portfolioId}/analytics/attribution?period=${period}`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

export const getAnalyticsDrawdown = (token, portfolioId) => fetch(`${API_URL}/api/portfolios/${portfolioId}/analytics/drawdown`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

export const getAnalyticsSectorHeatmap = (token, portfolioId) => fetch(`${API_URL}/api/portfolios/${portfolioId}/analytics/sector_heatmap`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

// Compliance & Risk
export const getComplianceCheck = (token, portfolioId) => fetch(`${API_URL}/api/portfolios/${portfolioId}/compliance/check`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

export const getComplianceRiskMetrics = (token, portfolioId) => fetch(`${API_URL}/api/portfolios/${portfolioId}/compliance/risk_metrics`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

export const getComplianceRules = (token) => fetch(`${API_URL}/api/users/compliance_rules`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

export const saveComplianceRule = (token, rule) => fetch(`${API_URL}/api/users/compliance_rules`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify(rule),
}).then(handleResponse);

export const deleteComplianceRule = (token, ruleId) => fetch(`${API_URL}/api/users/compliance_rules/${ruleId}`, {
  method: 'DELETE',
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

// Earnings & Events Calendar
export const getCalendarEvents = (token, source, portfolioId) => {
  let url = `${API_URL}/api/calendar/events?source=${source}`;
  if (portfolioId) url += `&portfolio_id=${portfolioId}`;
  return fetch(url, { headers: { 'Authorization': `Bearer ${token}` } }).then(handleResponse);
};

export const getTickerEvents = (token, ticker) => fetch(`${API_URL}/api/calendar/events/${ticker}`, {
  headers: { 'Authorization': `Bearer ${token}` }
}).then(handleResponse);

// Reconciliation
export const reconcilePortfolio = (token, portfolioId, positions, brokerFormat) => fetch(`${API_URL}/api/portfolios/${portfolioId}/reconcile`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ positions, broker_format: brokerFormat }),
}).then(handleResponse);

export const applyReconcileFixes = (token, portfolioId, fixes) => fetch(`${API_URL}/api/portfolios/${portfolioId}/reconcile/apply_fix`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ fixes }),
}).then(handleResponse);


// ===================================================================
//  MARKET INSIGHTS
// ===================================================================

export const getInsightSources = (token) => fetch(`${API_URL}/api/insights/sources`, {
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

export const generateInsight = (token, { sources, format, model, style_key, language }) => fetch(`${API_URL}/api/insights/generate`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ sources, format, model, style_key, language }),
}).then(handleResponse);

export const getMyInsights = (token) => fetch(`${API_URL}/api/insights/`, {
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

export const getInsight = (token, insightId) => fetch(`${API_URL}/api/insights/${insightId}`, {
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

export const refineInsight = (token, insightId, feedback) => fetch(`${API_URL}/api/insights/${insightId}/refine`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ feedback }),
}).then(handleResponse);

export const updateInsight = (token, insightId, updates) => fetch(`${API_URL}/api/insights/${insightId}`, {
  method: 'PUT',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify(updates),
}).then(handleResponse);

export const deleteInsight = (token, insightId) => fetch(`${API_URL}/api/insights/${insightId}`, {
  method: 'DELETE',
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

export const createManualDraft = (token, title) => fetch(`${API_URL}/api/insights/create-draft`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ title }),
}).then(handleResponse);

export const translateInsight = (token, insightId, language = 'Chinese') => fetch(`${API_URL}/api/insights/${insightId}/translate`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ language }),
}).then(handleResponse);

export const getWritingStyles = (token) => fetch(`${API_URL}/api/insights/writing-styles`, {
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

export const saveWritingStyle = (token, { sample_text, domain_key, label }) => fetch(`${API_URL}/api/insights/writing-styles`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ sample_text, domain_key, label }),
}).then(handleResponse);

export const deleteWritingStyle = (token, domainKey) => fetch(`${API_URL}/api/insights/writing-styles/${domainKey}`, {
  method: 'DELETE',
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

// Insight Track Record
export const addInsightPosition = (token, insightId, { ticker, direction, rationale }) => fetch(`${API_URL}/api/insights/${insightId}/add-position`, {
  method: 'POST',
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ ticker, direction, rationale }),
}).then(handleResponse);

export const getInsightPositions = (token, insightId) => fetch(`${API_URL}/api/insights/${insightId}/positions`, {
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

export const getInsightTrackRecord = () => fetch(`${API_URL}/api/insights/track-record`).then(handleResponse);

export const deleteInsightPosition = (token, positionId) => fetch(`${API_URL}/api/insights/track-record/${positionId}`, {
  method: 'DELETE',
  headers: { 'Authorization': `Bearer ${token}` },
}).then(handleResponse);

// Public insights (no auth)
export const getPublicInsights = (limit = 20) => fetch(`${API_URL}/api/insights/public?limit=${limit}`).then(handleResponse);
export const getPublicInsight = (insightId) => fetch(`${API_URL}/api/insights/public/${insightId}`).then(handleResponse);